import { useMemo } from 'react'
import type { TaskLabelDocument } from '@/lib/db/rxdb/schemas/taskLabel'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import {
  useLabels as useRxDBLabels,
  useTaskLabels as useRxDBTaskLabels,
} from '@/lib/db/rxdb/hooks'

/**
 * Hook for fetching task label assignments via RxDB.
 *
 * @param taskId - Task ID
 * @param workspaceId - Current workspace ID
 *
 * @example
 * const { taskLabels, labels } = useTaskLabels(taskId, workspaceId)
 */
export function useTaskLabels(
  taskId: string | null | undefined,
  workspaceId: string | null | undefined,
) {
  // Get task labels from RxDB
  const {
    taskLabels: allTaskLabels,
    isLoading: isLoadingTaskLabels,
  } = useRxDBTaskLabels({
    taskId: taskId || undefined,
  })

  // Get all labels from RxDB
  const { labels: allLabels, isLoading: isLoadingLabels } = useRxDBLabels()

  // Filter task label assignments
  const taskLabels = useMemo(() => {
    if (!taskId || !workspaceId) {
      return []
    }

    return allTaskLabels
      .filter(
        (taskLabel: TaskLabelDocument) =>
          taskLabel.taskId === taskId &&
          taskLabel.envId === workspaceId,
      )
      .sort((a: TaskLabelDocument, b: TaskLabelDocument) => {
        return a.createdOn - b.createdOn
      })
  }, [taskId, workspaceId, allTaskLabels])

  // Get full label details by joining with labels collection
  const labels = useMemo(() => {
    if (taskLabels.length === 0) {
      return []
    }

    const labelsById = new Map(allLabels.map((label) => [label.id, label]))

    return taskLabels
      .map((taskLabel: TaskLabelDocument) => {
        const label = labelsById.get(taskLabel.labelId)
        if (label) {
          return label
        }

        return {
          id: taskLabel.labelId,
          envId: taskLabel.envId,
          name: taskLabel.labelName || 'Untitled label',
          color: taskLabel.labelColor || '#gray',
          description: null,
          projectId: null,
          labelGroupId: taskLabel.labelGroupId ?? null,
          sortOrder: null,
          isDeleted: false,
          createdOn: taskLabel.createdOn,
          createdBy: taskLabel.createdBy,
          updatedOn: taskLabel.updatedOn,
          updatedBy: taskLabel.updatedBy,
          deletedOn: null,
          deletedBy: null,
        } satisfies LabelDocument
      })
      .sort((a: LabelDocument, b: LabelDocument) => {
        // Sort by sortOrder if available, otherwise by name
        const aOrder = (a as any).sortOrder
        const bOrder = (b as any).sortOrder
        if (aOrder && bOrder) {
          return aOrder.localeCompare(bOrder)
        }
        return a.name.localeCompare(b.name)
      })
  }, [allLabels, taskLabels])

  return {
    taskLabels,
    labels,
    isLoading: isLoadingTaskLabels || isLoadingLabels,
  }
}

/**
 * Hook for checking if a specific label is assigned to a task
 *
 * @param taskId - Task ID
 * @param labelId - Label ID
 * @param workspaceId - Current workspace ID
 *
 * @example
 * const { isAssigned } = useIsLabelAssigned(taskId, labelId, workspaceId)
 */
export function useIsLabelAssigned(
  taskId: string | null | undefined,
  labelId: string | null | undefined,
  workspaceId: string | null | undefined,
) {
  // Get task labels from RxDB
  const { taskLabels: allTaskLabels, isLoading } =
    useRxDBTaskLabels({
      taskId: taskId || undefined,
      labelId: labelId || undefined,
    })

  const isAssigned = useMemo(() => {
    if (!taskId || !labelId || !workspaceId) {
      return false
    }

    return allTaskLabels.some(
      (taskLabel: TaskLabelDocument) =>
        taskLabel.taskId === taskId &&
        taskLabel.labelId === labelId &&
        taskLabel.envId === workspaceId,
    )
  }, [taskId, labelId, workspaceId, allTaskLabels])

  return {
    isAssigned,
    isLoading,
  }
}

// Legacy exports removed - useTaskLabels is the primary export above
